import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Tabs,
  Tab,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  Skeleton,
  TablePagination,
} from "@mui/material";
import {
  AddOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import { User, parseError } from "../../utils";
import { useMonitors } from "./hooks/useMonitors";
import { useMentions } from "./hooks/useMentions";
import { KeywordMonitor, MentionResult, MonitorPlatform } from "./types";
import MonitorCard from "./components/MonitorCard";
import MentionCard from "./components/MentionCard";
import StatsOverview from "./components/StatsOverview";
import MonitorFormDialog from "./dialogs/MonitorFormDialog";
import MentionDetailDialog from "./dialogs/MentionDetailDialog";
import {
  MENTION_STATUS_NEW,
  MENTION_STATUS_SEEN,
  MENTION_STATUS_DISMISSED,
  MENTION_STATUS_ACTIVE,
  KEYWORD_MONITOR_PLATFORM_REDDIT,
  KEYWORD_MONITOR_PLATFORM_HACKERNEWS,
  KEYWORD_MONITOR_PLATFORM_LINKEDIN,
  KEYWORD_MONITOR_PLATFORM_X,
  KEYWORD_MONITOR_PLATFORM_QUORA,
} from "../../constants";

interface MonitoringPageProps {
  currentUser: User | any;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
}

const MonitoringPage: React.FC<MonitoringPageProps> = ({
  currentUser,
  companyId,
  getToken,
  eventListener,
}) => {
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Monitor state
  const [monitorFormOpen, setMonitorFormOpen] = useState(false);
  const [editingMonitor, setEditingMonitor] = useState<KeywordMonitor | null>(null);
  const [selectedMonitorId, setSelectedMonitorId] = useState<string>("");

  // Mention state
  const [selectedMention, setSelectedMention] = useState<MentionResult | null>(null);
  const [mentionDetailOpen, setMentionDetailOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>(MENTION_STATUS_ACTIVE);
  const [platformFilter, setPlatformFilter] = useState<string>("");

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);


  // Hooks
  const {
    monitors,
    loading: monitorsLoading,
    error: monitorsError,
    listMonitors,
    createMonitor,
    updateMonitor,
    deleteMonitor,
    pauseMonitor,
    resumeMonitor,
    triggerRun,
  } = useMonitors(getToken, companyId);

  const {
    mentions,
    loading: mentionsLoading,
    error: mentionsError,
    total: mentionsTotal,
    stats,
    listMentions,
    dismissMention,
    generateReplies,
    getStats,
  } = useMentions(getToken, companyId);

  // Load data on mount and tab change
  useEffect(() => {
    if (currentUser?.uid) {
      loadData();
    }
  }, [currentUser?.uid, companyId]);

  useEffect(() => {
    if (activeTab === 1) {
      loadMentions();
    }
  }, [activeTab, selectedMonitorId, statusFilter, platformFilter, page, rowsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [selectedMonitorId, statusFilter, platformFilter]);

  const loadData = async () => {
    try {
      await Promise.all([
        listMonitors(),
        getStats(),
      ]);
    } catch (err) {
      console.error("Failed to load monitoring data:", err);
    }
  };

  const loadMentions = async () => {
    try {
      await listMentions({
        monitorId: selectedMonitorId || undefined,
        status: (statusFilter || undefined) as "new" | "seen" | "dismissed" | "active" | undefined,
        platform: (platformFilter || undefined) as MonitorPlatform | undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
    } catch (err) {
      console.error("Failed to load mentions:", err);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const handleCreateMonitor = () => {
    setEditingMonitor(null);
    setMonitorFormOpen(true);
  };

  const handleEditMonitor = (monitor: KeywordMonitor) => {
    setEditingMonitor(monitor);
    setMonitorFormOpen(true);
  };

  const handleSaveMonitor = async (data: any) => {
    if (editingMonitor) {
      await updateMonitor(editingMonitor.id, data);
      eventListener({ type: "SET_SUCCESS", message: "Monitor updated successfully" });
    } else {
      await createMonitor(data);
      eventListener({ type: "SET_SUCCESS", message: "Monitor created successfully" });
    }
    await listMonitors();
  };

  const handleDeleteMonitor = async (monitor: KeywordMonitor) => {
    if (window.confirm(`Are you sure you want to delete "${monitor.name}"?`)) {
      try {
        await deleteMonitor(monitor.id);
        eventListener({ type: "SET_SUCCESS", message: "Monitor deleted successfully" });
        await listMonitors();
      } catch (err) {
        eventListener({ type: "SET_ERROR", error: parseError(err) });
      }
    }
  };

  const handlePauseMonitor = async (monitor: KeywordMonitor) => {
    try {
      await pauseMonitor(monitor.id);
      eventListener({ type: "SET_SUCCESS", message: "Monitor paused" });
      await listMonitors();
    } catch (err) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    }
  };

  const handleResumeMonitor = async (monitor: KeywordMonitor) => {
    try {
      await resumeMonitor(monitor.id);
      eventListener({ type: "SET_SUCCESS", message: "Monitor resumed" });
      await listMonitors();
    } catch (err) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    }
  };

  const handleRunNow = async (monitor: KeywordMonitor) => {
    try {
      await triggerRun(monitor.id);
      eventListener({ type: "SET_SUCCESS", message: "Monitor run triggered" });
    } catch (err) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    }
  };

  const handleMonitorClick = (monitor: KeywordMonitor) => {
    setSelectedMonitorId(monitor.id);
    setActiveTab(1);
  };

  const handleMentionClick = (mention: MentionResult) => {
    setSelectedMention(mention);
    setMentionDetailOpen(true);
  };

  const handleDismissMention = async (mention: MentionResult) => {
    try {
      await dismissMention(mention.id);
      await loadMentions();
      await getStats();
    } catch (err) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    }
  };

  const handleGenerateReplies = async (mentionId: string, context: string) => {
    return generateReplies(mentionId, { context });
  };

  const handleRefresh = async () => {
    if (activeTab === 0) {
      await listMonitors();
    } else {
      await loadMentions();
    }
    await getStats();
  };

  return (
    <Box>
      <StatsOverview stats={stats} loading={monitorsLoading && !stats} />

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2 }}
      >
        <Tab label="Monitors" />
        <Tab label="Mentions" />
      </Tabs>

      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshOutlined />}
          onClick={handleRefresh}
          disabled={monitorsLoading || mentionsLoading}
        >
          Refresh
        </Button>
        {activeTab === 0 && (
          <Button
            variant="contained"
            startIcon={<AddOutlined />}
            onClick={handleCreateMonitor}
          >
            Create Monitor
          </Button>
        )}
      </Box>

      {(monitorsError || mentionsError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {monitorsError || mentionsError}
        </Alert>
      )}

      {/* Monitors Tab */}
      {activeTab === 0 && (
        <Box>
          {monitorsLoading && monitors.length === 0 ? (
            <Grid container spacing={2}>
              {[1, 2, 3].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
                </Grid>
              ))}
            </Grid>
          ) : monitors.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                px: 2,
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No monitors yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first keyword monitor to start tracking mentions across Reddit and Hacker News
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddOutlined />}
                onClick={handleCreateMonitor}
              >
                Create Monitor
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {monitors.map((monitor) => (
                <Grid item xs={12} sm={6} md={4} key={monitor.id}>
                  <MonitorCard
                    monitor={monitor}
                    onEdit={handleEditMonitor}
                    onDelete={handleDeleteMonitor}
                    onPause={handlePauseMonitor}
                    onResume={handleResumeMonitor}
                    onRunNow={handleRunNow}
                    onClick={handleMonitorClick}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Mentions Tab */}
      {activeTab === 1 && (
        <Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 3,
              flexWrap: "wrap",
            }}
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Monitor</InputLabel>
              <Select
                value={selectedMonitorId}
                label="Monitor"
                onChange={(e) => setSelectedMonitorId(e.target.value)}
              >
                <MenuItem value="">All Monitors</MenuItem>
                {monitors.map((monitor) => (
                  <MenuItem key={monitor.id} value={monitor.id}>
                    {monitor.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={MENTION_STATUS_ACTIVE}>Active</MenuItem>
                <MenuItem value={MENTION_STATUS_NEW}>New</MenuItem>
                <MenuItem value={MENTION_STATUS_SEEN}>Seen</MenuItem>
                <MenuItem value={MENTION_STATUS_DISMISSED}>Dismissed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Platform</InputLabel>
              <Select
                value={platformFilter}
                label="Platform"
                onChange={(e) => setPlatformFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={KEYWORD_MONITOR_PLATFORM_REDDIT}>Reddit</MenuItem>
                <MenuItem value={KEYWORD_MONITOR_PLATFORM_HACKERNEWS}>Hacker News</MenuItem>
                <MenuItem value={KEYWORD_MONITOR_PLATFORM_LINKEDIN}>LinkedIn</MenuItem>
                <MenuItem value={KEYWORD_MONITOR_PLATFORM_X}>X</MenuItem>
                <MenuItem value={KEYWORD_MONITOR_PLATFORM_QUORA}>Quora</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {mentionsLoading && mentions.length === 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={150} sx={{ borderRadius: 1 }} />
              ))}
            </Box>
          ) : mentions.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                px: 2,
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No mentions found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {monitors.length === 0
                  ? "Create a monitor first to start tracking mentions"
                  : "Your monitors run every hour. Check back later or try different filters."}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {mentions.map((mention) => (
                  <MentionCard
                    key={mention.id}
                    mention={mention}
                    onClick={handleMentionClick}
                    onDismiss={handleDismissMention}
                  />
                ))}
              </Box>
              <TablePagination
                component="div"
                count={mentionsTotal}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ mt: 2, pb: 4 }}
              />
            </>
          )}
        </Box>
      )}

      {/* Monitor Form Dialog */}
      <MonitorFormDialog
        open={monitorFormOpen}
        onClose={() => setMonitorFormOpen(false)}
        onSave={handleSaveMonitor}
        monitor={editingMonitor}
        companyId={companyId}
        getToken={getToken}
      />

      {/* Mention Detail Dialog */}
      <MentionDetailDialog
        open={mentionDetailOpen}
        onClose={() => {
          setMentionDetailOpen(false);
          loadMentions(); // Refresh to update status
        }}
        mention={selectedMention}
        onDismiss={handleDismissMention}
        onGenerateReplies={handleGenerateReplies}
      />
    </Box>
  );
};

export default MonitoringPage;
